import { useMemo, useState, useRef } from 'react';
import { makeStyles } from '@material-ui/styles';
import cn from 'clsx';
import { MinimalEmoji, useEmojiContext } from 'stream-chat-react';
import { Tooltip } from '@bizzabo/design-system';

import type { BaseEmoji, Data as EmojiData } from 'emoji-mart';
import type { NimbleEmojiProps } from 'emoji-mart';

type ReactionEmoji = BaseEmoji | MinimalEmoji;

const MAX_USERS_ON_REACTION_TOOLTIP = 7;

const getStrippedEmojiData = (data: EmojiData) => ({
    ...data,
    emojis: {},
});

interface CustomReactionItemProps {
    key;
    reactionType: string;
    propReactionOptions: ReactionEmoji[];
    reactionUsersMap;
    reactionCounts: Record<string, number>;
    ownReactionTypes: string[];
    additionalEmoji?: Partial<NimbleEmojiProps>;
    onReactionClick?: (reactionType, event) => void;
}

const CustomReactionItem: React.FC<CustomReactionItemProps> = ({
    reactionType,
    ownReactionTypes,
    propReactionOptions,
    reactionCounts,
    additionalEmoji,
    reactionUsersMap,
    key,
    onReactionClick = () => null,
}) => {
    const [emojiId, setEmojiId] = useState(null);
    const [showTooltip, setShowTooltip] = useState(null);
    const anchorRef = useRef(null);
    const { Emoji, emojiConfig } = useEmojiContext();
    const { defaultMinimalEmojis, emojiData: fullEmojiData, emojiSetDef } = emojiConfig || {};
    const reactionOptions = propReactionOptions || defaultMinimalEmojis;
    const reactionsAreCustom = !!propReactionOptions?.length;
    const emojiData = useMemo(
        () => (reactionsAreCustom ? fullEmojiData : getStrippedEmojiData(fullEmojiData)),
        [fullEmojiData, reactionsAreCustom]
    );

    const isMyReaction = (reactionType: string, ownReactionTypes: string[]): boolean => {
        return ownReactionTypes.some(type => type === reactionType);
    };

    const getUserNamesFromReaction = reactionId => {
        if (!reactionUsersMap[reactionId]) {
            return '';
        }
        const totalUsers = reactionUsersMap[reactionId].length;

        let usersReaction = reactionUsersMap[reactionId]
            .map(user => user.firstName + ' ' + user.lastName)
            .slice(0, MAX_USERS_ON_REACTION_TOOLTIP)
            .join(', ');

        if (totalUsers > MAX_USERS_ON_REACTION_TOOLTIP) {
            usersReaction = `${usersReaction} (+${totalUsers - MAX_USERS_ON_REACTION_TOOLTIP})`;
        }

        return usersReaction;
    };

    const getEmojiByReactionType = (type: string): ReactionEmoji | undefined => {
        return reactionOptions.find((option: ReactionEmoji) => option.id === type);
    };

    const handleMouseEnter = emojiObjectId => {
        setEmojiId(emojiObjectId);
        setShowTooltip(true);
    };

    const emojiObject = getEmojiByReactionType(reactionType);
    const classes = useStyles();
    const tooltipContent = getUserNamesFromReaction(emojiId);

    if (!emojiObject) {
        return null;
    }

    return (
        <li
            className={cn(classes.reactionItem, {
                [classes.border]: isMyReaction(reactionType, ownReactionTypes),
            })}
            ref={anchorRef}
            onMouseEnter={() => handleMouseEnter(emojiObject.id)}
            onMouseLeave={() => setShowTooltip(false)}
            key={key}
            onClick={event => onReactionClick(reactionType, event)}
        >
            {tooltipContent !== '' && (
                <Tooltip
                    anchorRef={anchorRef}
                    boundariesElement="scrollParent"
                    open={showTooltip}
                    setOpen={setEmojiId}
                    text={tooltipContent}
                />
            )}
            <span className="emoji">
                {/* Web partners doesn't renderer the emoji well => they crash the app */}
                {/* this way app not crushing and user will be able to use chat */}
                {/* when user use emoji all is being renderer */}
                {/* i need to solve it but for now i will leave it with Emoji._result !== null  */}
                {/* maybe the best fix for it is by fixing another issue when we fetch the emoji on demand */}
                {/* we should fetch it on load to give user better experience */}
                {/* so maybe when i fix it it will fix this issue as well */}
                {/* eslint-disable-next-line @typescript-eslint/ban-ts-comment */}
                {/*@ts-ignore*/}
                {Emoji._result !== null && (
                    <Emoji
                        data={emojiData}
                        emoji={emojiObject}
                        size={13}
                        {...(reactionsAreCustom ? additionalEmoji : emojiSetDef)}
                    />
                )}
            </span>

            <span className={classes.count}>{reactionCounts[reactionType]}</span>
        </li>
    );
};

const useStyles = makeStyles(
    theme => ({
        reactionItem: {
            width: 39,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: 22,
            borderRadius: '300px',
            marginLeft: '4px',
            padding: '4px 12px',
            backgroundColor: theme.palette.greys[200],
            border: 'solid 1px transparent',
            cursor: 'pointer',
            transition: 'all 0.2s linear',
            '& span': {
                lineHeight: 1,
                transition: 'all 0.2s linear',
            },
            '&:hover': {
                '& .emoji': {
                    transform: 'scale(1.2)',
                },
            },
        },
        border: {
            borderColor: theme.customization.interactive.C10,
        },
        count: {
            ...theme.typography.t81,
            marginLeft: 4,
            color: theme.palette.greys[500],
        },
    }),
    { classNamePrefix: 'custom-reaction-item' }
);

export default CustomReactionItem;
